import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  FullAsinWithAudits,
  RestrictedAsin,
} from "../../server/asin-central/model";
import { porKey } from "../porAsinList/PorAsinListTable-config";
import logger from "../../art-logger-config";
import { getFromLocalStorage } from "../../util/localStorageUtils";
import { AppThunk } from "../app/store";
import { createNotificationDefinition } from "../notification/notificationConnector";
import {
  getBaselineRequest,
  getCandidateDates,
  recordAsinMarketplaceDates,
} from "../app/api";
import {
  AsinMarketplaces,
  BaselineRequest,
  CandidateDate,
  GetCandidateDatesOutput,
} from "@amzn/coral_com-amazon-deviceslaunchcontrolservice";
import {
  CandidateDateStatus,
  CandidateDateWithDescriptor,
  endGetCandidateDates,
  setCandidateDates,
  setProgramId,
  setScheduleId,
} from "./candidateDatesConnector";
import { AsinMarketplaceDates } from "@amzn/coral_com-amazon-deviceslaunchcontrolservice/dist/umd/AsinMarketplaceDates";
import { DateTypes } from "@amzn/coral_com-amazon-deviceslaunchcontrolservice/dist/umd/DateTypes";
import { baselineTableColumnDefinitions } from "./baselineTable-config";

interface AsinBaselineTableControlState {
  preferences: {
    pageSize: number;
    visibleContent: string[];
  };
  sort?: { sortingField: string };
  descending: boolean;
  baselineInProgress: boolean;
  baselineError: string | null;
  // Editing
  edited: boolean;
  isEditing: boolean;
  editedRowSet: { [index: number]: boolean };
  editableBaselineAsins: Array<FullAsinWithAudits | RestrictedAsin>;
  attributeIndexMap: { [asinMp: string]: number };
  pageNumber: number;
  existingBaselineRequest?: BaselineRequest;
}

function getInitialPreferences() {
  const defaultInitialPreferences = {
    pageSize: 30,
    visibleContent: baselineTableColumnDefinitions.options.flatMap((item) =>
      item.options.filter((c) => c.defaultVisible).map((c) => c.id)
    ),
  };

  try {
    const localPrefsString = getFromLocalStorage("BASELINE_TABLE_PREFERENCES");
    if (localPrefsString !== null) {
      return JSON.parse(localPrefsString);
    }
  } catch (e: any) {
    logger.error(
      `Failed to load initial preferences for baseline table: ${e.toString()}`
    );
  }
  return defaultInitialPreferences;
}

const asinBaselineTableControlInitialState: AsinBaselineTableControlState = {
  preferences: getInitialPreferences(),
  descending: true,
  baselineInProgress: false,
  baselineError: null,
  edited: false,
  isEditing: false,
  editedRowSet: {},
  editableBaselineAsins: [],
  attributeIndexMap: {},
  pageNumber: 1,
};

const asinBaselineTableControl = createSlice({
  name: "asinBaselineTableControl",
  initialState: asinBaselineTableControlInitialState,
  reducers: {
    setPreferences(state, action) {
      state.preferences.pageSize = action.payload.pageSize;
      state.preferences.visibleContent = action.payload.visibleContent;
    },
    mergeVisibleContentPreferences(state, action) {
      const activeColumns = action.payload?.activeColumns ?? [];
      state.preferences.visibleContent = Array.from(
        new Set<string>([...state.preferences.visibleContent, ...activeColumns])
      );
    },
    setSort(state, action) {
      state.sort = action.payload.sortingColumn;
      state.descending = action.payload.isDescending;
    },
    baselineAsinMarketplacesStart(state) {
      state.baselineInProgress = true;
    },
    baselineAsinMarketplacesSuccess(state) {
      state.baselineInProgress = false;
    },
    baselineAsinMarketplacesFailure(state, action: PayloadAction<string>) {
      state.baselineInProgress = false;
      state.baselineError = action.payload;
    },
    setEditingBaselineTable(state, { payload }) {
      state.isEditing = true;
      state.editedRowSet = {};
      state.editableBaselineAsins = [...payload.currentPagePorAsins];
      state.editableBaselineAsins.forEach((value, index) => {
        state.attributeIndexMap[porKey(value)] = index;
      });
    },
    setViewingBaselineTable(state) {
      state.isEditing = false;
      state.attributeIndexMap = {};
      state.edited = false;
      state.editableBaselineAsins = [];
    },
    setBaselinePageNumber(state, action) {
      state.pageNumber = action.payload.pageNumber;
    },
    setBaselineRequest(state, action) {
      state.existingBaselineRequest = action.payload;
    },
  },
});

export const {
  setPreferences,
  mergeVisibleContentPreferences,
  setSort,
  baselineAsinMarketplacesStart,
  baselineAsinMarketplacesFailure,
  baselineAsinMarketplacesSuccess,
  setEditingBaselineTable,
  setViewingBaselineTable,
  setBaselinePageNumber,
  setBaselineRequest,
} = asinBaselineTableControl.actions;

export default asinBaselineTableControl.reducer;

export const fetchGetCandidateDates =
  (asinMarketplaces: AsinMarketplaces): AppThunk =>
  async (dispatch) => {
    // Create the dictionary then store it directly in redux in one go.
    const candidateDateDictionary: {
      [key: string]: CandidateDateWithDescriptor[];
    } = {};

    function addToDictionary(
      key: string,
      value: CandidateDateWithDescriptor
    ): void {
      candidateDateDictionary[key] = [
        ...(candidateDateDictionary[key] || []),
        value,
      ];
    }

    function getDateWithDescriptor(
      date: CandidateDate
    ): CandidateDateWithDescriptor {
      return {
        dateValueInMillis: date.date?.dateValue,
        dateStatus: date.date?.status as CandidateDateStatus,
        descriptor: date.descriptor,
        marketplaceId: date.obfuscatedMarketplaceId,
      };
    }

    function handleCandidateDates(candidateDates: GetCandidateDatesOutput) {
      candidateDates!!.asinMarketplaceCandidateDates?.forEach(
        (asinMarketplaceCandidateDate) => {
          asinMarketplaceCandidateDate.candidateAnnounceDates?.forEach(
            (date) => {
              addToDictionary(
                `${asinMarketplaceCandidateDate.asin}_${asinMarketplaceCandidateDate.obfuscatedMarketplaceId}_announceDate`,
                getDateWithDescriptor(date)
              );
            }
          );
          asinMarketplaceCandidateDate.candidateStreetDates?.forEach((date) =>
            addToDictionary(
              `${asinMarketplaceCandidateDate.asin}_${asinMarketplaceCandidateDate.obfuscatedMarketplaceId}_onlineStreetDate`,
              getDateWithDescriptor(date)
            )
          );
        }
      );

      // Set the entire dictionary in redux
      dispatch(setCandidateDates(candidateDateDictionary));

      const candidateDateList = candidateDates!!.asinMarketplaceCandidateDates;
      if (candidateDateList !== undefined && candidateDateList?.length > 0) {
        const firstDate = candidateDateList[0];
        dispatch(setProgramId(firstDate.programId ?? ""));
        if (
          firstDate.candidateAnnounceDates !== undefined &&
          firstDate.candidateAnnounceDates?.length > 0
        ) {
          dispatch(
            setScheduleId(firstDate.candidateAnnounceDates[0].scheduleId ?? "")
          );
        }
      }
    }

    await getCandidateDates(asinMarketplaces)
      .then((candidateDates) => {
        handleCandidateDates(candidateDates);
      })
      .catch((reject) => {
        dispatch(
          createNotificationDefinition(
            "error",
            "Get Candidate Dates Error",
            true,
            `${reject.response.data.message.toString()}`
          )
        );
        dispatch(setViewingBaselineTable());
        return;
      })
      .finally(() => dispatch(endGetCandidateDates()));
  };

export const fetchRecordAsinMarketplaceDates =
  (
    asinMarketplaceDates: AsinMarketplaceDates,
    dateTypes: DateTypes,
    scheduleId?: string,
    programId?: string,
    baselineRequest?: BaselineRequest
  ): AppThunk =>
  async (dispatch) => {
    try {
      const response = await recordAsinMarketplaceDates(
        asinMarketplaceDates,
        dateTypes,
        scheduleId,
        programId,
        baselineRequest
      ).then((baselineRequest) =>
        dispatch(setBaselineRequest(baselineRequest))
      );
      return response;
    } catch (err: any) {
      dispatch(
        createNotificationDefinition(
          "error",
          "Record Dates Error",
          true,
          `Please ensure all required dates are valid and try again. If the issue persists, use give feedback in footer to report an issue.`
        )
      );
    }
  };
const errorMessage = `Next Steps:
- Ensure Baseline ID exists and is accessible
- Review ASIN-Central-Date-Baseline and request permisisons if needed
- Try refreshing the page
- Use give feedback in footer to report an issue`;

export const fetchBaselineRequest =
  (baselineId: string): AppThunk =>
  async (dispatch) => {
    try {
      return await getBaselineRequest(baselineId).then((baselineRequest) =>
        dispatch(setBaselineRequest(baselineRequest))
      );
    } catch (err: any) {
      dispatch(
        createNotificationDefinition(
          "error",
          "Get BaselineRequest Error",
          true,
          errorMessage
        )
      );
    }
  };
